<script setup lang="ts">
  import { useFsfStore } from '@/components/fsf/stores/fsf.store'
  import { useFlightStore } from '@/components/fsf/stores/flight.store'
  import { useFsfHelpers } from '@/components/fsf/composables/useFsfHelpers'
  import { storeToRefs } from 'pinia'
  import { ISO_DATE_FORMAT, ISO_YEAR_AND_MONTH_FORMAT, FULL_MONTH_YEAR_FORMAT } from '@/utils/constants/date'
  import { INBOUND } from '@/utils/constants/fsf'
  import useFSFDataLayer from '@/composables/data-layers/useFSFDataLayer'
  import type { IOutboundFlight, IInboundFlight } from '@/components/fsf/types'
  import type { IDay } from '@/components/calendar/types'
  import { useRecentSearch } from '@/composables/fsf/useRecentSearch'

  const { addToRecentSearch } = useRecentSearch()

  const dayjs = useDayjs()

  const { isDesktop } = useBTBreakpoints()
  const fsfStore = useFsfStore()
  const flightStore = useFlightStore()
  const { calendarFlights, noData } = useFsfHelpers()

  const nextButton = ref<HTMLButtonElement | null>(null)
  const prevButton = ref<HTMLButtonElement | null>(null)

  const mobileVisibleMonthsCount = ref(2)

  const { selectedStartDate, selectedEndDate, selectedMonth, origin, destin, currentFlightType } = storeToRefs(fsfStore)

  const hasOriginDestinSelected = computed(() => origin.value && destin.value)
  watch(
    () => selectedStartDate.value,
    (date) => {
      if (date) {
        onStartDateSelected(date)
      }
    }
  )

  const onStartDateSelected = (date: string) => {
    if (!fsfStore.isRoundTrip) {
      return
    }

    fsfStore.currentFlightType = INBOUND
    flightStore.getInboundFlights(date)
  }

  /**
   * START Select date
   */
  const hoveredDate = ref()
  const isRangeSelect = computed(() => !fsfStore.isOutboundFlightType)
  const rangeEndDate = computed(() => hoveredDate.value || selectedEndDate.value)

  const toggleSelection = (date: string) => {
    if (!noData.value && !calendarFlights.value?.find((f) => f.date === date)) {
      return
    }

    if (!isRangeSelect.value || !selectedStartDate.value || dayjs(date).isBefore(selectedStartDate.value)) {
      selectedStartDate.value = date
      selectedEndDate.value = undefined

      if (!fsfStore.isRoundTrip) {
        addToRecentSearch()
      }
    } else {
      selectedEndDate.value = date
      addToRecentSearch()
    }
  }
  /** Select date END **/

  const monthList = computed(() => {
    const start = dayjs(fsfStore.currentDateRange.startDate)
    const end = dayjs(fsfStore.currentDateRange.endDate)
    const months: string[] = []
    let currentMonth = start

    while (currentMonth.isBefore(end) || currentMonth.isSame(end)) {
      months.push(currentMonth.format(ISO_YEAR_AND_MONTH_FORMAT))
      currentMonth = currentMonth.add(1, 'month')
    }

    return months
  })

  const monthsInPages = computed(() => {
    const pages: string[][] = []
    monthList.value.forEach((m, i) => {
      monthList.value[i + 1] && pages.push([m, monthList.value[i + 1]])
    })

    return pages
  })

  const currentPageIndex = computed(() =>
    monthsInPages.value.findIndex(
      (page, i) =>
        page.includes(selectedMonth.value) &&
        (page.indexOf(selectedMonth.value) === 0 || i === monthsInPages.value.length - 1)
    )
  )

  const visibleMonths = computed<string[]>(() => {
    if (isDesktop.value) {
      return monthList.value.slice(currentPageIndex.value, currentPageIndex.value + 2)
    }

    return monthList.value.slice(0, mobileVisibleMonthsCount.value)
  })

  const formatMonth = (month: string) => {
    return dayjs(month).format(FULL_MONTH_YEAR_FORMAT)
  }

  /**
   * START Header
   */
  const currentMonthIndex = computed(() => monthList.value.findIndex((month) => month === selectedMonth.value))

  const canMoveBackward = computed(() => {
    return currentPageIndex.value - 1 >= 0
  })

  function isCheapestFlight(price: number | null, month: string, isOutboundFlightType: boolean) {
    if (fsfStore.hideFsfPrices || !fsfStore.showPrices) {
      return false
    }

    if (isOutboundFlightType) {
      const cheapestInboundPricesByMonth = flightStore.cheapestInboundPricesByMonth?.[month]
      return cheapestInboundPricesByMonth !== null && cheapestInboundPricesByMonth === price
    } else {
      const cheapestOutboundPricesByMonth = flightStore.cheapestOutboundPricesByMonth?.[month]
      return cheapestOutboundPricesByMonth !== null && cheapestOutboundPricesByMonth === price
    }
  }

  function isStartCell(cellDate: string) {
    return selectedStartDate.value === cellDate
  }

  function isEndCell(cellDate: string) {
    if (!isRangeSelect.value) {
      return false
    }

    if (dayjs(cellDate).isBefore(selectedStartDate.value, 'day')) return false

    return [selectedEndDate.value, hoveredDate.value].some((date) => date === cellDate)
  }

  function isCellInRange(cellDate: string) {
    if (!isRangeSelect.value) {
      return false
    }

    return (
      dayjs(cellDate).isSameOrAfter(selectedStartDate.value, 'day') &&
      dayjs(cellDate).isSameOrBefore(rangeEndDate.value, 'day')
    )
  }

  function moveBackward() {
    if (!canMoveBackward.value) {
      return
    }

    selectedMonth.value = monthList.value[currentMonthIndex.value - 1]
  }

  const canMoveForward = computed(() => {
    return monthsInPages.value.length - 1 !== currentPageIndex.value
  })

  function moveForward() {
    if (!canMoveForward.value) {
      return
    }

    selectedMonth.value = monthList.value[currentMonthIndex.value + 1]
  }
  /** HEADER END **/

  const { pushFSFDataLayer } = useFSFDataLayer()

  const { t } = useI18n()
  type FlightType = IOutboundFlight | IInboundFlight
  const getFlightAriaLabel = (flight: FlightType, item: IDay) => {
    const date = dayjs(item.date).format('LL')
    const price = flight.price ? `${flight.price} EUR` : t('fsf.calendar.legends.priceNotAvailable')
    const flightType = t(`fsf.calendar.legends.${flight.isDirect ? 'direct' : 'connecting'}`)

    return [date, price, flightType].filter(Boolean).join(', ')
  }

  const sentinel = ref<HTMLElement | null>(null)
  let observer: IntersectionObserver | null = null

  const loadMoreMonths = () => {
    if (mobileVisibleMonthsCount.value < monthList.value.length) {
      mobileVisibleMonthsCount.value += 2
    }
  }

  const ensureSelectedMonthVisibleOnMobile = async (month?: string) => {
    if (isDesktop.value) {
      return
    }

    const selectedStartMonth = selectedStartDate.value
      ? dayjs(selectedStartDate.value).format(ISO_YEAR_AND_MONTH_FORMAT)
      : null

    const monthToShow = month || selectedStartMonth || selectedMonth.value

    if (!monthToShow) {
      return
    }

    const index = monthList.value.findIndex((m) => m === monthToShow)
    if (index === -1) {
      return
    }

    const neededCount = Math.max(2, index + 2)
    if (mobileVisibleMonthsCount.value < neededCount) {
      mobileVisibleMonthsCount.value = neededCount
      await nextTick()
    }

    const target = document.getElementById(`calendar-${monthToShow}`)
    const scrollContainer = document.querySelector('[data-mobile-calendar-scroll]') as HTMLElement | null

    if (!target || !scrollContainer) {
      return
    }

    const MONTH_TITLE_HEIGHT = 56
    const targetOffset =
      target.getBoundingClientRect().top -
      scrollContainer.getBoundingClientRect().top +
      scrollContainer.scrollTop -
      MONTH_TITLE_HEIGHT

    scrollContainer.scrollTo({ top: targetOffset, behavior: 'smooth' })
  }

  watch(
    () => selectedMonth.value,
    (month) => {
      ensureSelectedMonthVisibleOnMobile(month)
    },
    { flush: 'post' }
  )

  onMounted(() => {
    pushFSFDataLayer({
      generic_event_name: 'fsf_select_date',
      event_category: 'flight_search',
      origin_airport_code: origin.value,
      destination_airport_code: destin.value,
      trip_type: currentFlightType.value
    })

    if (selectedStartDate.value || selectedMonth.value) {
      ensureSelectedMonthVisibleOnMobile()
    }

    if (!isDesktop.value && typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const scrollContainer = document.querySelector('[data-mobile-calendar-scroll]')

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadMoreMonths()
            }
          })
        },
        {
          root: scrollContainer,
          rootMargin: '0px 0px 40px 0px',
          threshold: 0
        }
      )

      if (sentinel.value) {
        observer.observe(sentinel.value)
      }
    }
  })

  onBeforeUnmount(() => {
    if (observer && sentinel.value) {
      observer.unobserve(sentinel.value)
    }
    observer = null
  })

  const hidePrices = computed(() => fsfStore.hideFsfPrices || !fsfStore.showPrices)
</script>

<template>
  <section
    :aria-label="$t('fsf.calendar.legends.flightCalendar')"
    class="flex flex-col gap-6"
  >
    <div
      v-if="isDesktop"
      class="grid grid-cols-2 gap-11"
    >
      <div class="flex items-center justify-center">
        <UiButton
          ref="prevButton"
          variant="outline"
          icon="common/chevron-left"
          :aria-label="$t('aria.label.prevMonth')"
          aria-hidden="true"
          :disabled="!canMoveBackward"
          @click="moveBackward"
        />
        <p class="mb-0 grow text-center capitalize text-h5">
          {{ formatMonth(visibleMonths[0]) }}
        </p>
      </div>
      <div class="flex items-center justify-center">
        <p class="mb-0 grow text-center capitalize text-h5">
          {{ formatMonth(visibleMonths[1]) }}
        </p>
        <UiButton
          ref="nextButton"
          variant="outline"
          icon="common/chevron-right"
          :aria-label="$t('aria.label.nextMonth')"
          aria-hidden="true"
          :disabled="!canMoveForward"
          @click="moveForward"
        />
      </div>
    </div>
    <div
      id="fsf-calenadar"
      class="flex flex-col justify-between space-y-4 px-4 pt-4 sm:px-4 md:flex-row md:gap-9 md:space-x-4 md:space-y-0 md:p-0 lg:gap-11 lg:px-0 lg:py-0 xl:gap-20"
    >
      <template
        v-for="month in visibleMonths"
        :key="`range-${month}`"
      >
        <div
          v-if="!isDesktop"
          class="flex items-center"
        >
          <p class="mb-0 mt-3 grow text-center capitalize text-h5">
            {{ formatMonth(month) }}
          </p>
        </div>

        <Calendar
          :id="`calendar-${month}`"
          :month="month"
          :cell-slot-date-format="ISO_DATE_FORMAT"
          :disable-past-dates="true"
        >
          <template
            v-for="flight in calendarFlights"
            #[flight.date]="{ item }"
            :key="flight.date"
          >
            <FsfCalendarCellContent
              :is-inbound="!fsfStore.isOutboundFlightType"
              :date="item.displayDate"
              :is-direct="flight.isDirect"
              :price="flight.price"
              :is-cheapest-flight="isCheapestFlight(flight.price, month, !fsfStore.isOutboundFlightType)"
              :is-start="isStartCell(item.cellSlotDate)"
              :is-end="isEndCell(item.cellSlotDate)"
              :is-in-range="isCellInRange(item.cellSlotDate)"
              :disabled="item.isDisabled"
              :show-empty="false"
              :aria-label="getFlightAriaLabel(flight, item)"
              :hide-prices="hidePrices"
              :empty-cell-content="!fsfStore.showPrices"
              @click="toggleSelection(item.cellSlotDate)"
              @mouseleave="hoveredDate = undefined"
              @mouseover="hoveredDate = item.cellSlotDate"
              @focus="hoveredDate = item.cellSlotDate"
              @blur="hoveredDate = undefined"
            />
          </template>

          <template #default="{ item }">
            <FsfCalendarEmptyCell
              v-if="dayjs(item.cellSlotDate).isBefore(dayjs(), 'day') || !hasOriginDestinSelected"
              :display-date="item.displayDate"
              :aria-label="dayjs(item.date).format('LL') + ' ' + $t('fsf.calendar.empty')"
            />
            <template v-else>
              <FsfCalendarCellContent
                v-if="item.isCurrentMonth"
                :disabled="item.isDisabled"
                :date="item.displayDate"
                :is-start="isStartCell(item.cellSlotDate)"
                :is-end="isEndCell(item.cellSlotDate)"
                :is-in-range="isCellInRange(item.cellSlotDate)"
                :empty-cell-content="!fsfStore.showPrices"
                no-flight
                :no-data="noData"
                :aria-label="dayjs(item.date).format('LL') + ' ' + $t('fsf.calendar.noFlight')"
                @click="toggleSelection(item.cellSlotDate)"
                @mouseleave="hoveredDate = undefined"
                @mouseover="hoveredDate = item.cellSlotDate"
                @focus="hoveredDate = item.cellSlotDate"
                @blur="hoveredDate = undefined"
              />
            </template>
          </template>
        </Calendar>
      </template>
      <div
        v-if="!isDesktop"
        ref="sentinel"
        class="h-1 w-full"
      />
    </div>
  </section>
</template>
